import React from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ZAxis, Cell } from 'recharts';
import { Map, AlertTriangle, ZapOff, TrendingUp, ArrowRight } from 'lucide-react';
// Mock Data
const congestionZones = [{
  zone: 'North-South',
  load: 98,
  limit: 100,
  cost: 4500
}, {
  zone: 'East-West',
  load: 45,
  limit: 100,
  cost: 0
}, {
  zone: 'Coastal',
  load: 102,
  limit: 100,
  cost: 12000
}, {
  zone: 'Central',
  load: 85,
  limit: 100,
  cost: 1500
}];
const redispatchHistory = Array.from({
  length: 24
}, (_, i) => ({
  hour: `${i}:00`,
  volume: i > 10 && i < 18 ? Math.random() * 500 : 0,
  cost: i > 10 && i < 18 ? Math.random() * 500 * 80 : 0
}));
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-1222">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-1223">{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-1224">
            <span className="text-slate-600 capitalize" data-id="element-1225">{entry.name}:</span>
            <span className="font-mono font-medium" data-id="element-1226">
              {entry.value.toFixed(0)}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function CongestionAnalysisDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-red-50/30" data-id="element-1227">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1228">
        <div data-id="element-1229">
          <div className="flex items-center gap-2 mb-1" data-id="element-1230">
            <div className="p-2 bg-red-500 rounded-lg" data-id="element-1231">
              <Map size={20} className="text-white" data-id="element-1232" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-1233">
              Congestion Analysis
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-1234">
            Network bottlenecks, redispatch costs, and constraint management
          </p>
        </div>

        <div className="flex items-center gap-4" data-id="element-1235">
          <div className="text-right" data-id="element-1236">
            <p className="text-xs text-slate-500" data-id="element-1237">Total Redispatch Cost</p>
            <p className="text-lg font-bold text-red-600" data-id="element-1238">€18,450</p>
          </div>
          <div className="text-right" data-id="element-1239">
            <p className="text-xs text-slate-500" data-id="element-1240">Active Constraints</p>
            <p className="text-lg font-bold text-slate-900" data-id="element-1241">2 Zones</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1242">
        {/* Critical Corridors */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1243">
          <h3 className="text-lg font-bold text-slate-900 mb-6" data-id="element-1244">
            Critical Corridors Load
          </h3>
          <div className="space-y-6" data-id="element-1245">
            {congestionZones.map(zone => <div key={zone.zone} data-id="element-1246">
                <div className="flex justify-between text-sm mb-2" data-id="element-1247">
                  <span className="font-medium text-slate-700" data-id="element-1248">
                    {zone.zone}
                  </span>
                  <span className={`font-bold ${zone.load > 100 ? 'text-red-600' : 'text-slate-600'}`} data-id="element-1249">
                    {zone.load}%
                  </span>
                </div>
                <div className="h-4 bg-slate-100 rounded-full overflow-hidden relative" data-id="element-1250">
                  <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400 left-[100%] z-10" data-id="element-1251"></div>
                  <motion.div initial={{
                width: 0
              }} animate={{
                width: `${Math.min(zone.load, 100)}%`
              }} className={`h-full rounded-full ${zone.load > 95 ? 'bg-red-500' : zone.load > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-1252" />
                </div>
                {zone.cost > 0 && <div className="mt-1 text-xs text-red-600 flex items-center gap-1" data-id="element-1253">
                    <AlertTriangle size={10} data-id="element-1254" /> Redispatch Cost: €{zone.cost}
                  </div>}
              </div>)}
          </div>
        </motion.div>

        {/* Redispatch Volumes */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1255">
          <div className="flex justify-between items-center mb-6" data-id="element-1256">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-1257">
              Redispatch Volumes
            </h3>
            <span className="text-xs text-slate-500" data-id="element-1258">MWh per hour</span>
          </div>

          <div className="h-[300px] w-full" data-id="element-1259">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1260">
              <BarChart data={redispatchHistory} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-1261">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-1262" />
                <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={3} data-id="element-1263" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} data-id="element-1264" />
                <Tooltip content={<CustomTooltip data-id="element-1266" />} data-id="element-1265" />
                <Bar dataKey="volume" fill="#ef4444" radius={[2, 2, 0, 0]} data-id="element-1267" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Locational Pricing */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-1268">
        <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-1269">
          Locational Marginal Pricing (LMP)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1270">
          {[{
          node: 'Node A (Gen)',
          price: 35.2,
          type: 'low'
        }, {
          node: 'Node B (Load)',
          price: 42.5,
          type: 'mid'
        }, {
          node: 'Node C (Congested)',
          price: 85.0,
          type: 'high'
        }, {
          node: 'Node D (Export)',
          price: 41.8,
          type: 'mid'
        }].map(node => <div key={node.node} className={`p-4 rounded-lg border ${node.type === 'high' ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`} data-id="element-1271">
              <p className="text-xs text-slate-500 mb-1" data-id="element-1272">{node.node}</p>
              <p className={`text-xl font-bold ${node.type === 'high' ? 'text-red-700' : 'text-slate-900'}`} data-id="element-1273">
                €{node.price.toFixed(2)}
              </p>
            </div>)}
        </div>
      </motion.div>
    </div>;
}